import type { ComputedRef, MaybeRefOrGetter, Ref } from 'vue';
import { find } from 'lodash-es';
import { computed, ref, toRef, toValue } from 'vue';

import type { FormFlattenKey, FormFlattenValue, FormResult, VFormRef } from '@modules/forms/components/VForm/types';
import { useCountriesQuery } from '@modules/countries/queries';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';
import { TRANSLATION_GROUP_PAYMENT_SYSTEMS } from '@modules/payment-systems/constants';
import { isArray } from '@utils/guards';
import { watchImmediate } from '@vueuse/core';

import type {
  IAffiliateAllowedPaymentSystem,
  ICurrentPaymentInfo,
  IPaymentSystemFieldNames,
  IPaymentSystemFormSchema,
  IPaymentSystemsFields,
  IPaymentSystemsFormValues,
  PaymentSystemName,
} from '../types';
import { PAYMENT_SYSTEMS } from '../constants';
import { useAllowedPaymentSystemsQuery } from '../queries';
import useCurrentPaymentSystem from './useCurrentPaymentSystem';

export interface IPaymentSystemFormComposable {
  paymentSettingsForm: Ref<VFormRef | undefined>;
  currentFormValues: ComputedRef<ICurrentPaymentInfo | undefined>;
  paymentSystemSchema: ComputedRef<IPaymentSystemFormSchema>;
  allowedPaymentSystemsList: ComputedRef<IAffiliateAllowedPaymentSystem[] | undefined>;
  paymentSystemsNamesList: ComputedRef<PaymentSystemName[]>;
  isDataLoading: Ref<boolean>;
  changeCurrentFormValues(systemInfo?: ICurrentPaymentInfo): void;
  resetFormValues(): void;
  onFormChange<
    T extends IPaymentSystemsFields,
    K extends FormFlattenKey<T>,
  >(
    key: K,
    value: FormFlattenValue<T, K> | undefined,
    result: FormResult<IPaymentSystemsFields>,
  ): void;
  onSubmit(): void;
}

export default function usePaymentSystemForm(
  paymentSystemName: Ref<PaymentSystemName | undefined>,
  paymentSystem?: MaybeRefOrGetter<ICurrentPaymentInfo | undefined>,
  copy: MaybeRefOrGetter<boolean> = false,
): IPaymentSystemFormComposable {
  const paymentSettingsForm = ref<VFormRef>();
  const formValues = ref<IPaymentSystemsFormValues>(getDefaultFormValues());
  const paymentSystemRef = toRef(paymentSystem);

  const { getTranslation } = useI18n();

  const { data: countries, isLoading: isLoadingCountries } = useCountriesQuery();
  const { data: allowedPaymentSystems, isLoading: isAllowedSystemsLoading } = useAllowedPaymentSystemsQuery();

  const { currentPaymentSystem: selectedPaymentSystemSettings } = useCurrentPaymentSystem(paymentSystemName);

  watchImmediate(paymentSystemRef, (newValues) => {
    changeCurrentFormValues(newValues);
  });

  const isDataLoading = computed(() => isLoadingCountries.value || isAllowedSystemsLoading.value);

  const allowedPaymentSystemsList = computed<IAffiliateAllowedPaymentSystem[] | undefined>(() => allowedPaymentSystems.value?.payment_systems);

  const paymentSystemsNamesList = computed<PaymentSystemName[]>(() => {
    return allowedPaymentSystemsList.value?.flatMap((item) => Object.keys(item) as PaymentSystemName[]) ?? [];
  });

  const currentFormValues = computed<ICurrentPaymentInfo | undefined>(() => paymentSystemName.value ? formValues.value[paymentSystemName.value] : undefined);

  const fullPaymentSystemSchema = computed<IPaymentSystemFormSchema>(() => ({
    country_code: {
      type: EFormInputTypes.SELECT,
      options: countries.value ?? [],
      optionValue: 'country_code',
      optionLabel: 'name',
      value: currentFormValues.value?.country_code,
    },
    account_holders_name: {
      type: EFormInputTypes.TEXT,
      value: currentFormValues.value?.account_holders_name || '',
    },
    branch_code: {
      type: EFormInputTypes.TEXT,
      value: currentFormValues.value?.branch_code || '',
    },
    routing_number: {
      type: EFormInputTypes.TEXT,
      value: currentFormValues.value?.routing_number || '',
    },
    username: {
      type: EFormInputTypes.TEXT,
      value: currentFormValues.value?.username || '',
    },
    poker_nickname: {
      type: EFormInputTypes.TEXT,
      value: currentFormValues.value?.poker_nickname || '',
    },
    email: {
      type: EFormInputTypes.TEXT,
      value: currentFormValues.value?.email || '',
    },
    wallet: {
      type: EFormInputTypes.TEXT,
      value: currentFormValues.value?.wallet || '',
    },
    card_number: {
      type: EFormInputTypes.TEXT,
      value: currentFormValues.value?.card_number || '',
    },
    country: {
      type: EFormInputTypes.TEXT,
      value: currentFormValues.value?.country || '',
    },
    address: {
      type: EFormInputTypes.TEXT,
      value: currentFormValues.value?.address || '',
    },
    city: {
      type: EFormInputTypes.TEXT,
      value: currentFormValues.value?.city || '',
    },
    company_name: {
      type: EFormInputTypes.TEXT,
      value: currentFormValues.value?.company_name || '',
    },
    company_country: {
      type: EFormInputTypes.TEXT,
      value: currentFormValues.value?.company_country || '',
    },
    company_city: {
      type: EFormInputTypes.TEXT,
      value: currentFormValues.value?.company_city || '',
    },
    company_address: {
      type: EFormInputTypes.TEXT,
      value: currentFormValues.value?.company_address || '',
    },
    company_postal: {
      type: EFormInputTypes.TEXT,
      value: currentFormValues.value?.company_postal || '',
    },
    iban: {
      type: EFormInputTypes.TEXT,
      value: currentFormValues.value?.iban || '',
    },
    bic: {
      type: EFormInputTypes.TEXT,
      value: currentFormValues.value?.bic || '',
    },
    bank_name: {
      type: EFormInputTypes.TEXT,
      value: currentFormValues.value?.bank_name || '',
    },
    bank_country: {
      type: EFormInputTypes.TEXT,
      value: currentFormValues.value?.bank_country || '',
    },
    bank_address: {
      type: EFormInputTypes.TEXT,
      value: currentFormValues.value?.bank_address || '',
    },
    bank_city: {
      type: EFormInputTypes.TEXT,
      value: currentFormValues.value?.bank_city || '',
    },
    swift: {
      type: EFormInputTypes.TEXT,
      value: currentFormValues.value?.swift || '',
    },
    phone_number: {
      type: EFormInputTypes.TEXT,
      value: currentFormValues.value?.phone_number || '',
    },
  }));

  const paymentSystemSchema = computed<IPaymentSystemFormSchema>(() => {
    const schema: IPaymentSystemFormSchema = {};
    if (!selectedPaymentSystemSettings.value || !paymentSystemName.value) {
      return schema;
    }

    const requiredFieldsData = find(allowedPaymentSystemsList.value, paymentSystemName.value)?.[paymentSystemName.value]?.required;
    let requiredFieldsList: IPaymentSystemFieldNames[] = isArray(requiredFieldsData) ? requiredFieldsData : [];

    if (paymentSystemName.value === 'Bank transfer' && requiredFieldsData && !isArray(requiredFieldsData) && currentFormValues.value?.country_code) {
      requiredFieldsList = requiredFieldsData[currentFormValues.value.country_code] ?? [];
    }

    for (const [field, options] of Object.entries(selectedPaymentSystemSettings.value.fields)) {
      if (isPaymentSystemField(field)) {
        const allField = fullPaymentSystemSchema.value[field];
        const opts = requiredFieldsList.includes(field) ? { required: true } : options;

        if (allField) {
          Object.assign(schema, {
            [field]: {
              ...allField,
              ...opts,
              copy: toValue(copy),
              label: getTranslation(TRANSLATION_GROUP_PAYMENT_SYSTEMS, `${selectedPaymentSystemSettings.value.localizationShortCode}${field}`).value,
            },
          });
        }
      }
    }

    return schema;
  });

  function onFormChange<
    T extends IPaymentSystemsFields,
    K extends FormFlattenKey<T>,
  >(
    key: K,
    value: FormFlattenValue<T, K> | undefined,
    result: FormResult<IPaymentSystemsFields>,
  ): void {
    if (paymentSystemName.value) {
      formValues.value[paymentSystemName.value] = {
        ...result.values,
        name: paymentSystemName.value,
      };
    }
  }

  function onSubmit(): void {
    paymentSettingsForm.value?.submit();
  }

  function changeCurrentFormValues(systemInfo?: ICurrentPaymentInfo): void {
    if (paymentSystemName.value && systemInfo) {
      formValues.value[paymentSystemName.value] = systemInfo;
    }
  }

  function resetFormValues(): void {
    formValues.value = getDefaultFormValues();
  }

  function isPaymentSystemField(argument: string): argument is IPaymentSystemFieldNames {
    return Object.keys(fullPaymentSystemSchema.value).includes(argument);
  }

  function getDefaultFormValues(): IPaymentSystemsFormValues {
    return PAYMENT_SYSTEMS.map(({ name }) => name).reduce((acc, name) => {
      acc[name] = { name };
      return acc;
    }, {} as IPaymentSystemsFormValues);
  }

  return {
    paymentSettingsForm,
    currentFormValues,
    paymentSystemSchema,
    allowedPaymentSystemsList,
    paymentSystemsNamesList,
    isDataLoading,
    changeCurrentFormValues,
    resetFormValues,
    onFormChange,
    onSubmit,
  };
}
